import React, { useEffect, useRef, useState } from 'react';
import { Mic, Image as ImageIcon, FileText, Send, ChevronRight, Square, X, Camera } from 'lucide-react';
import ScreenHeader from '../ScreenHeader';
import AnonimizarFoto from './AnonimizarFoto';

const PHOTO_STOCK = [
  // Placeholders ilustrativos (preview UI). Las fotos reales se almacenan como File
  // en draft.evidencia.fotosFiles y se suben al bucket privado 'evidencias' al guardar.
  'linear-gradient(135deg, #EEEAFE 0%, #DCE7FF 100%)',
  'linear-gradient(135deg, #FFF1D6 0%, #FFE0DA 100%)',
  'linear-gradient(135deg, #DFF6EC 0%, #D6F3F3 100%)',
];

/**
 * Evidencia (§31). Texto / Voz / Foto.
 * - Si conducta fue "OTRA", la nota es obligatoria.
 * - Voz: contador local; el archivo de audio NO se sube en Etapa 6 (sólo segundos).
 * - Fotos: máx 5 — se almacenan como File para subir a Supabase Storage en el sync.
 * - Botón "Omitir" disponible si la conducta NO es "OTRA".
 */
const ScreenEvidencia = ({ onBack, onNext, onSkip, draft, update, isOther, appMode }) => {
  const [tab, setTab] = useState('nota');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timer = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);   // File pendiente de anonimizar

  useEffect(() => {
    if (recording) {
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    }
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [recording]);

  const stopRecording = () => {
    setRecording(false);
    update((d) => ({ ...d, evidencia: { ...d.evidencia, audioSeconds: seconds } }));
  };

  const addPhoto = () => {
    // Abre selector — la anonimización obligatoria se hace en el modal AnonimizarFoto.
    fileInputRef.current?.click();
  };


  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const currFotos = draft.evidencia?.fotos || [];
    if (currFotos.length >= 5) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Sólo procesamos la PRIMERA foto por vez — pasa por anonimización obligatoria.
    setPendingFile(files[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Callback cuando el modal de anonimización termina (foto ya validada en cliente + servidor)
  const onAnonimizarDone = (result) => {
    // result contiene: { staging_path, preview_url, rostros_cliente, rostros_servidor, ...}
    update((d) => {
      const currFotos = d.evidencia?.fotos || [];
      const currStaged = d.evidencia?.stagedFotos || [];
      return {
        ...d,
        evidencia: {
          ...d.evidencia,
          fotos: [...currFotos, PHOTO_STOCK[currFotos.length % PHOTO_STOCK.length]],
          stagedFotos: [...currStaged, result],
        },
      };
    });
    setPendingFile(null);
  };

  const removePhoto = (idx) => {
    update((d) => {
      const fotos = [...(d.evidencia?.fotos || [])];
      const staged = [...(d.evidencia?.stagedFotos || [])];
      fotos.splice(idx, 1);
      if (idx < staged.length) staged.splice(idx, 1);
      return { ...d, evidencia: { ...d.evidencia, fotos, stagedFotos: staged } };
    });
  };

  const nota = draft.evidencia?.nota || '';
  const fotos = draft.evidencia?.fotos || [];
  const audioSeconds = draft.evidencia?.audioSeconds || 0;

  const otraInvalid = isOther && !nota.trim();
  const formatSec = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="step-enter flex flex-col h-full">
      <ScreenHeader
        step={4}
        stepName="Evidencia"
        onBack={onBack}
        title="Evidencia"
        subtitle={isOther
          ? 'Como elegiste "Otra conducta", agrega una nota breve obligatoria.'
          : 'Opcional. Suma una nota, audio o foto si crees que ayudará.'}
        eta="≈ 15s restantes"
      />

      <div className="px-5">
        <div role="tablist" aria-label="Tipo de evidencia" className="grid grid-cols-3 gap-1.5 bg-[#F4F6FB] p-1 rounded-2xl">
          {[
            { v: 'nota', label: 'Nota', Icon: FileText },
            { v: 'voz', label: 'Voz', Icon: Mic },
            { v: 'foto', label: 'Foto', Icon: ImageIcon },
          ].map(({ v, label, Icon }) => (
            <button
              key={v}
              role="tab"
              aria-selected={tab === v}
              data-testid={`evidencia-tab-${v}`}
              onClick={() => setTab(v)}
              className={`tap-44 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl text-sm font-bold transition-all ${
                tab === v ? 'bg-white text-brand-violet shadow' : 'text-brand-muted'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-5 pt-5 overflow-y-auto">
        {tab === 'nota' && (
          <div data-testid="evidencia-nota-panel" className="tab-fade">
            <span className="m-label">
              Nota breve {isOther && <span className="text-[#C73838]">· obligatoria</span>}
            </span>
            <textarea
              data-testid="evidencia-nota"
              value={nota}
              onChange={(e) => update((d) => ({ ...d, evidencia: { ...d.evidencia, nota: e.target.value } }))}
              placeholder="Describe brevemente el hecho con lenguaje observable (sin diagnóstico)."
              rows={6}
              className="m-textarea resize-none"
            />
            <p className="mt-2 text-[11px] text-brand-muted">
              Recomendado: usa frases como &ldquo;se observa…&rdquo;, &ldquo;el estudiante relata…&rdquo;, &ldquo;varios estudiantes…&rdquo;.
            </p>
          </div>
        )}

        {tab === 'voz' && (
          <div data-testid="evidencia-voz-panel" className="tab-fade flex flex-col items-center justify-center text-center py-6">
            <button
              data-testid={recording ? 'evidencia-voz-stop' : 'evidencia-voz-record'}
              onClick={() => recording ? stopRecording() : (setSeconds(0), setRecording(true))}
              className="relative w-28 h-28 rounded-full flex items-center justify-center"
              style={{
                background: recording ? '#C73838' : '#5346E6',
                color: '#FFFFFF',
                boxShadow: '0 20px 40px -15px rgba(83, 70, 230, 0.55)',
              }}
              aria-label={recording ? 'Detener grabación' : 'Grabar audio'}
            >
              {recording ? <Square className="w-9 h-9" /> : <Mic className="w-10 h-10" />}
              {recording && (
                <>
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(199,56,56,0.35)' }} />
                  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(199,56,56,0.2)', animationDelay: '300ms' }} />
                </>
              )}
            </button>
            <p className="mt-5 text-2xl font-extrabold text-brand-deep tabular-nums">
              {recording ? formatSec(seconds) : (audioSeconds ? formatSec(audioSeconds) : '00:00')}
            </p>
            <p className="mt-1 text-xs text-brand-muted">
              {recording ? 'Grabando…' : (audioSeconds ? 'Audio guardado. Toca para regrabar.' : 'Toca para iniciar grabación.')}
            </p>
            {audioSeconds > 0 && !recording && (
              <button
                data-testid="evidencia-voz-delete"
                onClick={() => update((d) => ({ ...d, evidencia: { ...d.evidencia, audioSeconds: 0 } }))}
                className="mt-3 text-xs font-bold text-[#C73838]"
              >
                Eliminar audio
              </button>
            )}
          </div>
        )}

        {tab === 'foto' && (
          <div data-testid="evidencia-foto-panel" className="tab-fade">
            <span className="m-label">Fotografías · hasta 5</span>
            <div className="grid grid-cols-3 gap-2">
              {fotos.map((bg, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-[#DDE6F7]" style={{ background: bg }} data-testid={`evidencia-foto-${idx}`}>
                  <div className="absolute inset-0 flex items-center justify-center text-brand-violet">
                    <Camera className="w-6 h-6 opacity-60" />
                  </div>
                  <button
                    data-testid={`evidencia-foto-del-${idx}`}
                    onClick={() => removePhoto(idx)}
                    aria-label="Eliminar foto"
                    className="tap-44 absolute -top-2 -right-2 w-11 h-11 rounded-full bg-white text-[#C73838] flex items-center justify-center shadow-md hover:scale-105 transition-transform"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
              {fotos.length < 5 && (
                <button
                  data-testid="evidencia-foto-add"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#DDE6F7] hover:border-brand-violet text-brand-violet flex flex-col items-center justify-center gap-1 transition-colors"
                >
                  <Camera className="w-6 h-6" />
                  <span className="text-[11px] font-bold">Agregar</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={onPickFiles}
              className="hidden"
              data-testid="evidencia-foto-input"
            />
            <p className="mt-3 text-[11px] text-brand-muted">
              Resguarda la privacidad: evita rostros identificables y datos sensibles.
            </p>
          </div>
        )}
      </div>

      <div className="px-5 pb-6 pt-3 border-t border-[#F1F4FA] bg-white space-y-2">
        <button
          data-testid="evidencia-next"
          onClick={onNext}
          disabled={otraInvalid}
          className="m-btn-primary"
        >
          {otraInvalid ? 'Agrega la nota obligatoria' : 'Confirmar y guardar'}
          {!otraInvalid && <Send className="w-4 h-4" />}
        </button>
        {!isOther && (
          <button
            data-testid="evidencia-skip"
            onClick={onSkip}
            className="m-btn-ghost"
            type="button"
          >
            Omitir evidencia
          </button>
        )}
      </div>

      {pendingFile && (
        <AnonimizarFoto
          file={pendingFile}
          modo={appMode}
          onCancel={() => setPendingFile(null)}
          onDone={onAnonimizarDone}
        />
      )}
    </div>
  );
};

export default ScreenEvidencia;
